import type {
  ActivitySubjectActionSummary,
  ActivitySubjectCard,
  ActivitySubjectRecommendation,
} from '@shared/types/activity-feed';
import type {
  AccountThreadLightView,
  ThreadTopicSummary,
  ThreadVisibleState,
} from '@shared/types/thread-work';
import type { WorkActionPresentation } from '@shared/types/work-action';
import { getSubjectWorkActions } from '@/lib/work-actions';

export type ThreadHomeZone = 'now' | 'following' | 'verify' | 'quiet';

export interface ActivityLivingAccountProjection {
  /** Account/root subject used for copy and canonical Thread navigation. */
  subject: ActivitySubjectCard;
  /** Exact subject that owns the current topic's legacy-correlated CTA. */
  actionSubject: ActivitySubjectCard;
  /** Activity subjects collapsed into this Account Thread. */
  members: ActivitySubjectCard[];
}

export interface ActivityLivingWorkProjection {
  subject: ActivitySubjectCard;
  actionSubject: ActivitySubjectCard;
  topic: ThreadTopicSummary;
  work: WorkActionPresentation;
  zone: Exclude<ThreadHomeZone, 'quiet'>;
}

export const isUnattachedActivitySubject = (
  subject: Pick<ActivitySubjectCard, 'accountAttachmentStatus'>,
): boolean => subject.accountAttachmentStatus === 'unattached'
  || subject.accountAttachmentStatus === 'ambiguous';

export const THREAD_VISIBLE_STATE_LABELS: Record<ThreadVisibleState, string> = {
  actionable: 'À faire',
  needs_preparation: 'À préparer',
  running: 'Domino travaille',
  awaiting_review: 'À valider',
  waiting: 'En attente',
  monitoring: 'Sous surveillance',
  blocked: 'Bloqué',
  closed: 'Clos',
};

export const getThreadHomeZone = (
  threadState: AccountThreadLightView | null | undefined,
): ThreadHomeZone => {
  if (!threadState?.currentTopic) return 'quiet';
  return getThreadTopicHomeZone(threadState, threadState.currentTopic);
};

export const getThreadTopicHomeZone = (
  threadState: Pick<AccountThreadLightView, 'decisionHealth' | 'executionHealth'>,
  topic: ThreadTopicSummary,
): ThreadHomeZone => {
  if (
    threadState.decisionHealth === 'stale'
    || threadState.decisionHealth === 'unavailable'
    || threadState.executionHealth === 'failed'
  ) return 'verify';

  if (threadState.decisionHealth === 'refreshing') return 'following';

  if (
    topic.visibleState === 'running'
    || topic.visibleState === 'waiting'
    || topic.visibleState === 'monitoring'
  ) return 'following';

  if (topic.visibleState === 'closed') return 'quiet';
  return 'now';
};

const hasSameSourceIdentity = (
  topic: ThreadTopicSummary,
  candidate: {
    sourceRunId: string | null;
    sourceActionId: string | null;
  },
): boolean => (
  topic.sourceActionId !== null
  && candidate.sourceActionId === topic.sourceActionId
  && (
    topic.sourceRunId === null
    || candidate.sourceRunId === topic.sourceRunId
  )
);

export const findTopicActionSummary = (
  subject: ActivitySubjectCard,
  topic: ThreadTopicSummary | null,
): ActivitySubjectActionSummary | null => {
  if (!topic) return null;
  return (subject.recommendedActions ?? []).find((action) =>
    hasSameSourceIdentity(topic, action)
  ) ?? null;
};

export const findTopicRecommendation = (
  recommendations: ActivitySubjectRecommendation[],
  topic: ThreadTopicSummary | null,
): ActivitySubjectRecommendation | null => {
  if (!topic) return null;
  return recommendations.find((recommendation) =>
    hasSameSourceIdentity(topic, recommendation)
  ) ?? null;
};

/**
 * Activity still arrives as subject cards. P4 collapses cards that resolve to
 * the same persistent Thread without recomputing that Thread's priority. The
 * exact action-owning subject is retained separately for mutations/handoffs.
 */
export const projectActivityLivingAccounts = (
  subjects: ActivitySubjectCard[],
): ActivityLivingAccountProjection[] => {
  const groups = new Map<string, ActivitySubjectCard[]>();
  subjects.forEach((subject) => {
    const identity = subject.threadState?.threadId
      ? `thread:${subject.threadState.threadId}`
      : subject.accountSubjectId
      ? `account:${subject.accountSubjectId}`
      : `subject:${subject.subjectId}`;
    groups.set(identity, [...(groups.get(identity) ?? []), subject]);
  });

  return Array.from(groups.values()).flatMap((group) => {
    const fallbackSubject = group[0];
    if (!fallbackSubject) return [];
    const threadState = group.find((candidate) => candidate.threadState)?.threadState
      ?? null;
    const topic = threadState?.currentTopic ?? null;
    const actionSubject = group.find((candidate) => (
      findTopicActionSummary(candidate, topic) !== null
    )) ?? fallbackSubject;
    const accountSubject = group.find((candidate) => (
      candidate.accountSubjectId !== null
      && candidate.subjectId === candidate.accountSubjectId
    ));
    const subject = accountSubject ?? actionSubject;

    return {
      subject: subject.threadState === threadState
        ? subject
        : { ...subject, threadState },
      actionSubject,
      members: group,
    };
  });
};

export const getStableTimestamp = (value: string | null): number => {
  if (!value) return Number.MAX_SAFE_INTEGER;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};

/**
 * Expands each Account Thread into its active work topics. Priority is never
 * inferred from labels: the read model's zone and priority rank drive order.
 */
export const projectActivityLivingWorks = (
  subjects: ActivitySubjectCard[],
): ActivityLivingWorkProjection[] => {
  const zoneRank: Record<Exclude<ThreadHomeZone, 'quiet'>, number> = {
    now: 0,
    following: 1,
    verify: 2,
  };

  return projectActivityLivingAccounts(subjects)
    .flatMap((account) => {
      const threadState = account.subject.threadState;
      if (!threadState?.currentTopic) return [];
      const topics = [
        threadState.currentTopic,
        ...threadState.otherTopics.filter(
          (topic) => topic.id !== threadState.currentTopic?.id
        ),
      ];
      const members = account.members.map((subject) => ({
        subject,
        works: getSubjectWorkActions(subject),
      }));
      const accountWorks = getSubjectWorkActions(account.subject);

      return topics.flatMap((topic): ActivityLivingWorkProjection[] => {
        const zone = getThreadTopicHomeZone(threadState, topic);
        if (zone === 'quiet') return [];
        const actionOwner = members.find((candidate) =>
          findTopicActionSummary(candidate.subject, topic) !== null
        );
        const actionSubject = actionOwner?.subject ?? account.actionSubject;
        const work = actionOwner?.works.find((candidate) => candidate.id === topic.id)
          ?? accountWorks.find((candidate) => candidate.id === topic.id);
        if (!work) return [];
        const accountWork = work.context.decisionFocus
          && account.subject.accountSubjectId === account.subject.subjectId
          ? {
              ...work,
              context: {
                ...work.context,
                decisionFocus: {
                  ...work.context.decisionFocus,
                  accountLabel: account.subject.subjectLabel,
                },
              },
            }
          : work;
        return [{
          subject: account.subject,
          actionSubject,
          topic,
          work: accountWork,
          zone,
        }];
      });
    })
    .sort((left, right) => {
      const zoneDelta = zoneRank[left.zone] - zoneRank[right.zone];
      if (zoneDelta !== 0) return zoneDelta;
      const rankDelta = (left.topic.priorityRank ?? Number.MAX_SAFE_INTEGER)
        - (right.topic.priorityRank ?? Number.MAX_SAFE_INTEGER);
      if (rankDelta !== 0) return rankDelta;
      const dueDelta = getStableTimestamp(left.topic.dueAt)
        - getStableTimestamp(right.topic.dueAt);
      if (dueDelta !== 0) return dueDelta;
      const accountDelta = left.subject.subjectLabel.localeCompare(
        right.subject.subjectLabel,
        'fr'
      );
      if (accountDelta !== 0) return accountDelta;
      return left.topic.id.localeCompare(right.topic.id);
    });
};

export const getThreadTopicRationale = (topic: ThreadTopicSummary): string | null => (
  topic.decision?.reason
  ?? topic.stateReason
  ?? (topic.desiredOutcome !== topic.title ? topic.desiredOutcome : null)
);
